import { use } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PlaceholderContainer } from '@topcon/topcon-utils';
import { TeButton, TeContainer, TeContainerSlim, TeIcon, TeText } from '@topcon/tuicl-react';

import IconWrapper from '../../IconWrapper';
import { background, border } from '../../../providers/theme/theme.index';
import { ServiceAgreementTermsEnum } from '../../../api/generated/pm';
import { AppRoutes, useIsLogged } from '../../../app/routes/utils';
import { type ReviewResultContextType } from '../../../providers/ReviewResultContext/review-result.types';
import { ReviewResultContext } from '../../../providers/ReviewResultContext/review-result.context';
import AgreementAccessRight from '../AgreementAccessRight';
import { ReviewSource } from '../serviceAgreementReviewNavigation';
import ContinueCard from '../../common/ContinueCard/ContinueCard';
import CustomGrid from '../../common/CustomGrid/CustomGrid';
import TeContainerSlimWrapper from '../../common/PrimaryContainerSlim/PrimaryContainerSlim';
import { useServiceAgreementWithId } from '../../../pages/Customers/customers.queries';
import { useAgreementResultStatusText } from '../../../hooks/useAgreementResultStatusText';

function ServiceAgreementResultStep() {
  const { t } = useTranslation();
  const isLogged = useIsLogged();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const navigate = useNavigate();
  const { result } = use<ReviewResultContextType>(ReviewResultContext);
  const { data: serviceAgreementData, isLoading } = useServiceAgreementWithId(id);
  const serviceAgreementTerms = serviceAgreementData?.serviceAgreementTerms;
  const text = useAgreementResultStatusText({
    result,
    dealerName: serviceAgreementData?.provider?.name,
    customerName: serviceAgreementData?.customer?.name,
    terms: serviceAgreementTerms,
  });

  if (!id) {
    navigate(AppRoutes.Overview);
    return;
  }

  function handleContinue() {
    const flow = searchParams.get('flow');
    const source = searchParams.get('source');

    if (flow === 'review') {
      if (source === ReviewSource.Dealers) {
        navigate(AppRoutes.Dealers, { replace: true });
        return;
      }
      if (source === ReviewSource.Customers) {
        navigate(AppRoutes.Customers, { replace: true });
        return;
      }
    }
    if (result === 'accepted') {
      // change with notifications page
      navigate(AppRoutes.Invites);
      return;
    }
    navigate(AppRoutes.Overview);
  }

  const summaryLabel = t('serviceAgreement.agreementReview.summary', {
    name: serviceAgreementData?.provider?.name,
  });

  const AcceptedAgreementSection = () => {
    return (
      <TeContainerSlim
        fill
        grow
        column
        gap="l"
        borderColor={border.primary}
        borderRadius={4}
        padding="l"
        justifyContent="center"
        alignItems="center"
        width={576}
      >
        <TeText size={14} weight={400}>
          {summaryLabel}
        </TeText>
        <CustomGrid
          columns={serviceAgreementTerms === ServiceAgreementTermsEnum.FULL_SERVICE ? 3 : 1}
          gap={0}
          padding={0}
        >
          {serviceAgreementTerms === ServiceAgreementTermsEnum.FULL_SERVICE && (
            <AgreementAccessRight
              unsetWidth
              title={t('serviceAgreement.accessRights.activations.title')}
              description={t('serviceAgreement.accessRights.activations.description')}
            />
          )}
          {serviceAgreementTerms === ServiceAgreementTermsEnum.FULL_SERVICE && (
            <AgreementAccessRight
              unsetWidth
              title={t('serviceAgreement.accessRights.administration.title')}
              description={t('serviceAgreement.accessRights.administration.description')}
            />
          )}
          <AgreementAccessRight
            unsetWidth
            title={t('serviceAgreement.accessRights.accessData.title')}
            description={t('serviceAgreement.accessRights.accessData.description')}
          />
        </CustomGrid>
      </TeContainerSlim>
    );
  };

  const DeclinedAgreementSection = () => (
    <TeText size={12} weight={400} color="text.secondary">
      {t('clickToContinue')}
    </TeText>
  );

  return (
    <TeContainer fill grow justifyContent="center" alignItems="center">
      {isLoading ? (
        <PlaceholderContainer grow hasLoader text={t('loading')} />
      ) : (
        <TeContainerSlimWrapper borderRadius="m">
          <TeContainerSlim fill grow padding="xl">
            <TeContainerSlim fill grow column justifyContent="center" alignItems="center" gap="xs">
              <IconWrapper backgroundColor={background.tertiary} size={64}>
                <TeIcon
                  color={
                    result === 'accepted' ? 'icon.success.primary' : 'background.error.quaternary'
                  }
                  name={result === 'accepted' ? 'check_circle' : 'cancel'}
                  outline
                  size="xl"
                  style={{ scale: 1.5 }}
                />
              </IconWrapper>
              <TeContainer width={450} padding={['xs', 0, 'l', 0]}>
                <TeText weight={700} size={20} alignment="center">
                  {text}
                </TeText>
              </TeContainer>

              {result === 'accepted' ? <AcceptedAgreementSection /> : <DeclinedAgreementSection />}
              <TeContainerSlim
                padding={['m', 0]}
                gap="m"
                fill
                grow
                width={576}
                justifyContent="center"
              >
                {isLogged ? (
                  <TeButton text={t('continue')} size="s" teclick={handleContinue} />
                ) : (
                  <ContinueCard />
                )}
              </TeContainerSlim>
            </TeContainerSlim>
          </TeContainerSlim>
        </TeContainerSlimWrapper>
      )}
    </TeContainer>
  );
}

export default ServiceAgreementResultStep;
